/**
 * The update-required signal (D19, §R.117–§R.127).
 *
 * Two things raise it:
 *   - 'below-floor': the Android build's versionCode is below the known
 *     minimum (src/mobile/versionGuard.ts, src/mobile/useVersionGate.ts);
 *   - 'server-refused': a response carried the reserved SQLSTATE KA001 — the
 *     server refusing an out-of-date caller. That is the only real
 *     ENFORCEMENT; the client floor is an affordance that can be offline,
 *     stale or absent (§R.117).
 *
 * The signal is sticky for the life of the page: the native update screen has
 * no dismiss path, and a restart is what clears it (brief §4 item 6).
 *
 * This module is imported by the shared Supabase client, so it is in the WEB
 * bundle too. What the web does with the signal is a reload prompt
 * (src/components/WebUpdateRequiredPrompt.tsx); it never reaches the Play
 * Store (Codex round 135).
 */

/**
 * The reserved update-required SQLSTATE (§R.123). M7's refusal must RAISE
 * exactly this, and nothing else in the application may use it: a global
 * interceptor matching a code the app already returns (`55000`, `P0002`, …)
 * would turn ordinary errors from ANY call into a forced update (§R.121).
 */
export const UPDATE_REQUIRED_SQLSTATE = 'KA001';

export type UpdateRequiredReason = 'below-floor' | 'server-refused';

let current: UpdateRequiredReason | null = null;
const listeners = new Set<() => void>();

export function raiseUpdateRequired(reason: UpdateRequiredReason): void {
  if (current) return;
  current = reason;
  for (const l of listeners) l();
}

export function getUpdateRequired(): UpdateRequiredReason | null {
  return current;
}

export function subscribeUpdateRequired(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Tests only: the signal is otherwise cleared only by reloading the app. */
export function resetUpdateRequiredForTests(): void {
  current = null;
  for (const l of listeners) l();
}

/** True when a PostgREST (or edge function) error body carries exactly KA001. */
export function isUpdateRequiredBody(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === UPDATE_REQUIRED_SQLSTATE
  );
}

/**
 * A fetch wrapper for supabase-js's `global.fetch` (§R.121, §R.127).
 *
 * supabase-js RESOLVES with `{ data, error }` rather than throwing, and every
 * page is reused as-is, so no call site would ever notice a KA001: it would
 * render as an ordinary failure toast. The client's own fetch sees every
 * response whatever the call site. It clones before reading, parses only
 * non-OK JSON bodies, and fails silently on anything else, so the caller
 * receives the original response untouched.
 */
export function createUpdateRequiredFetch(baseFetch?: typeof fetch): typeof fetch {
  return async (input, init) => {
    const response = await (baseFetch ?? fetch)(input, init);
    if (!response.ok && /json/i.test(response.headers.get('content-type') ?? '')) {
      try {
        if (isUpdateRequiredBody(await response.clone().json())) raiseUpdateRequired('server-refused');
      } catch {
        // Not JSON after all, or an unreadable body: not our signal.
      }
    }
    return response;
  };
}
